import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { useFormik } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { date, object, ObjectSchema, string } from 'yup';
import ClientOnboardingApi from '../../../apis/ClientOnboardingApi';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import FormikDateField from '../../../components/FormikDateField/FormikDateField';
import FormikTextField from '../../../components/FormikTextField/FormikTextField';
import LoadingCircle from '../../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../../components/UnexpectedError/UnexpectedError';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { ClientOnboardingPreviewDto } from '../../../generated-types/clientOnboardingPreviewDto';
import { StartClientOnboardingDto } from '../../../generated-types/startClientOnboardingDto';
import useApi from '../../../lib/useApi';
import DataControl from '../Components/DataControl';

/**
 * A client onboarding subview
 */
const ClientOnboardingSubview = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [subDelay, setSubDelay] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUnrecognizedError, setShowUnrecognizedError] = useState(false);
  const [clientOnboardingToDelete, setClientOnboardingToDelete] =
    useState<ClientOnboardingPreviewDto | null>(null);
  const [courseOnboardingPreviewDtos, setCourseOnboardingPreviewDto] =
    useState<ClientOnboardingPreviewDto[]>();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const threeDaysFromNow = useMemo(() => {
    const today = new Date();
    return new Date(today.setDate(today.getDate() + 3));
  }, []);
  // The minimum three days needs to be right below since the yup date validation is > rather than >=
  const minThreeDays = useMemo(() => {
    const today = new Date();
    return new Date(today.setDate(today.getDate() + 2.99));
  }, []);
  const gridColumns: GridColDef<GridValidRowModel>[] = [
    {
      field: 'name',
      headerName: ' Name',
      flex: isMd ? 1 : undefined,
      width: isMd ? undefined : 200,
      renderCell: (params) => params.value ?? <i>Not signed up</i>,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: isMd ? 1 : undefined,
      width: isMd ? undefined : 300,
    },
    {
      field: 'currentStep',
      headerName: 'Current Step',
      flex: isMd ? 1 : undefined,
      width: isMd ? undefined : 200,
      renderCell: (params) => params.value ?? <i>N/A</i>,
    },
    {
      field: 'started',
      headerName: 'Started',
      flex: isMd ? 1 : undefined,
      width: isMd ? undefined : 200,
      type: 'dateTime',
      renderCell: (params) => formatDate('Not started', params),
    },
    {
      field: 'completed',
      headerName: 'Completed',
      flex: isMd ? 1 : undefined,
      width: isMd ? undefined : 200,
      type: 'dateTime',
      renderCell: (params) => {
        const row: ClientOnboardingPreviewDto =
          params.row as ClientOnboardingPreviewDto;

        if (row.completed == undefined && row.expired)
          return (
            <Box display="flex" gap=".5em" alignItems="center">
              <Typography fontSize={14}>Expired</Typography>
              <WarningAmberRoundedIcon color="warning" fontSize="small" />
            </Box>
          );

        return formatDate('Not complete', params);
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      flex: isMd ? 0.5 : undefined,
      width: isMd ? undefined : 50,
      renderCell: (params) => {
        const row = params.row as ClientOnboardingPreviewDto;
        const isComplete = row.completed != null;
        const tooltip = isComplete
          ? 'Cannot deleted completed onboarding'
          : 'Delete';

        return (
          <Tooltip title={tooltip} arrow placement="left">
            <span style={{ cursor: isComplete ? 'help' : 'pointer' }}>
              <IconButton
                onClick={() => {
                  setClientOnboardingToDelete(row);
                  setShowDeleteDialog(true);
                }}
                disabled={isComplete}
                color="error"
              >
                <DeleteRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];
  const formatDate = (
    missingDateMessage: string,
    params: GridRenderCellParams<
      GridValidRowModel,
      Date,
      any,
      GridTreeNodeWithRender
    >
  ) => {
    const date = params.value;
    return (
      date && date > new Date(1900, 1) ? (
        date.toLocaleString('en-US')
      ) : (
        <i>{missingDateMessage}</i>
      )
    ) as React.ReactNode;
  };

  const schema: ObjectSchema<StartClientOnboardingDto> = object({
    firstName: string().defined('Required'),
    lastName: string().defined('Required'),
    email: string().defined('Required').email('Email must be valid'),
    subscriptionDelay: date().min(
      minThreeDays,
      'Delay must be at least 3 days into the future'
    ),
  });
  const snackbarContext = useContext(SnackbarContext);
  const clientOnboardingApi = useApi(ClientOnboardingApi);
  const [showAddForm, setShowAddForm] = useState(false);

  /**
   * Load the client onboarding
   */
  const loadOnboarding = useCallback(() => {
    clientOnboardingApi
      .GetAllClientOnboarding()
      .then(setCourseOnboardingPreviewDto)
      .catch(() => setShowUnrecognizedError(true))
      .finally(() => setIsLoading(false));
  }, [clientOnboardingApi]);

  const formik = useFormik<StartClientOnboardingDto>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      subscriptionDelay: undefined,
    },
    validationSchema: schema,
    onSubmit: (val) => {
      clientOnboardingApi
        .StartClientOnboarding(val)
        .then((response) => {
          switch (response.code) {
            case 201:
            case 204:
              snackbarContext.setAlert(
                <Alert variant="filled" severity="success">
                  Client onboarding has successfully begun for {val.email}.
                </Alert>
              );
              loadOnboarding();
              setShowAddForm(false);
              formik.resetForm();
              break;
            case 409:
              snackbarContext.setAlert(
                <Alert variant="filled" severity="error">
                  Client onboarding has already been started for {val.email}.
                </Alert>
              );
              setShowAddForm(true);
              break;
            case 500:
              if (
                response.body == 'email-error-invite' ||
                response.body == 'email-error-notify'
              ) {
                const adjective =
                  response.body == 'email-error-invite' ? 'invite' : 'notify';
                snackbarContext.setAlert(
                  <Alert variant="filled" severity="warning">
                    An error occurred while sending the {adjective} email to{' '}
                    {val.email}. The client onboarding still begun successfully.
                  </Alert>
                );
                setShowAddForm(false);
                formik.resetForm();
              } else {
                throw Error();
              }
              break;
            default:
              throw Error();
          }

          setSubDelay(false);
          formik.setFieldValue('subscriptionDelay', undefined);
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert variant="filled" severity="error">
              An unrecognized error occurred while starting the client
              onboarding.
            </Alert>
          );
          setShowAddForm(true);
        })
        .finally(() => {
          snackbarContext.triggerSnackbar();
          formik.setSubmitting(false);
        });
    },
  });

  // Load the client onboarding on showing this component
  useEffect(() => {
    loadOnboarding();
  }, [loadOnboarding]);

  const addForm = useMemo(() => {
    return (
      <Container maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <Typography marginY="1em">
            Start client onboarding for this specified person. Once created,
            this will automatically send an email to the person inviting them to
            completed the onboarding.
          </Typography>
          <FormikTextField
            formik={formik}
            fullWidth
            label="Email"
            name="email"
            autoFocus
            sx={{
              marginBottom: '1em',
            }}
          />
          <FormikTextField
            formik={formik}
            fullWidth
            label="First Name"
            name="firstName"
            sx={{
              marginBottom: '1em',
            }}
          />
          <FormikTextField
            formik={formik}
            fullWidth
            label="Last Name"
            name="lastName"
            sx={{
              marginBottom: '1.5em',
            }}
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="1em"
            sx={{
              marginBottom: subDelay ? '1.5em' : 0,
            }}
          >
            <Box width={subDelay ? '60%' : 'initial'} marginBottom="1em">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={subDelay}
                    onChange={(e) => {
                      setSubDelay(e.target.checked);
                      if (!e.target.checked) {
                        formik.setFieldValue('subscriptionDelay', undefined);
                      } else {
                        formik.setFieldValue(
                          'subscriptionDelay',
                          threeDaysFromNow
                        );
                      }
                    }}
                  />
                }
                label="Delay the start of the subscription"
                disabled={formik.isSubmitting}
              />
            </Box>
            {subDelay && (
              <FormikDateField
                formik={formik}
                label="Subscription Delay"
                name="subscriptionDelay"
                minDate={threeDaysFromNow}
                sx={{
                  width: '40%',
                }}
              />
            )}
          </Box>
          <LoadingButton
            loading={formik.isSubmitting}
            loadingPosition="end"
            type="submit"
            variant="contained"
            endIcon={<SendRoundedIcon />}
          >
            Send
          </LoadingButton>
        </form>
      </Container>
    );
  }, [formik, subDelay, threeDaysFromNow]);

  return (
    <Box position="relative" height="100%" width="100%">
      {showUnrecognizedError ? (
        <UnexpectedError errorContext="the admin client onboarding page" />
      ) : (
        <>
          {courseOnboardingPreviewDtos && (
            <DataControl
              pageTitle="Client Onboarding"
              addTitle="Send Client Onboarding"
              addForm={addForm}
              gridColumns={gridColumns}
              rowData={courseOnboardingPreviewDtos}
              resetForm={() => {
                formik.resetForm();
                setSubDelay(false);
                formik.setFieldValue('subscriptionDelay', undefined);
              }}
              addShowing={showAddForm}
              showAddForm={() => setShowAddForm(true)}
              hideAdd={() => setShowAddForm(false)}
            />
          )}
          <LoadingCircle isLoading={isLoading} />
        </>
      )}
      <ConfirmDialog
        confirmAction={async () => {
          await clientOnboardingApi.deleteClientOnboarding(
            clientOnboardingToDelete!.id
          );
          setCourseOnboardingPreviewDto(
            (prev) =>
              prev?.filter((dto) => dto.id !== clientOnboardingToDelete!.id)
          );
          setClientOnboardingToDelete(null);
        }}
        confirmColor="error"
        confirmText="Delete"
        dialogBody={`Are you sure you want to delete the onboarding for ${clientOnboardingToDelete?.email}?`}
        dialogTitle="Delete Client Onboarding"
        errorMessage="Deleting the client onboarding was unsuccessful."
        successMessage="Successfully deleted client onboarding"
        onClose={() => setShowDeleteDialog(false)}
        showDialog={showDeleteDialog}
      />
    </Box>
  );
};

export default ClientOnboardingSubview;
